import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, RefreshCw, Mail } from "lucide-react";
import { API } from "../App";

/**
 * Two-factor authentication challenge screen.
 *
 * Props:
 *  - challengeId: string  — issued by /auth/login
 *  - maskedEmail: string  — e.g. "ad**n@gofood.com.br"
 *  - onVerified(data)     — callback when verification succeeds; receives the
 *                           full /auth/2fa/verify response (token, user, device_token)
 *  - onCancel()           — back to login form
 */
export default function TwoFAChallenge({ challengeId, maskedEmail, onVerified, onCancel }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const setDigit = (i, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    setDigits((arr) => {
      const next = [...arr];
      next[i] = v;
      return next;
    });
    if (v && i < 5) inputs.current[i + 1]?.focus();
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!txt) return;
    e.preventDefault();
    const arr = txt.split("");
    setDigits([
      arr[0] || "", arr[1] || "", arr[2] || "",
      arr[3] || "", arr[4] || "", arr[5] || "",
    ]);
    inputs.current[Math.min(arr.length, 5)]?.focus();
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    const code = digits.join("");
    if (code.length !== 6) {
      toast.error("Digite os 6 dígitos do código.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/2fa/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challenge_id: challengeId,
          code,
          remember_device: remember,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || "Código inválido.");
        setDigits(["", "", "", "", "", ""]);
        inputs.current[0]?.focus();
        return;
      }
      if (data.device_token) {
        try { localStorage.setItem("twofa_device_token", data.device_token); } catch { /* quota */ }
      }
      onVerified(data);
    } catch {
      toast.error("Erro ao verificar o código.");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/2fa/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challenge_id: challengeId }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.detail || "Não foi possível reenviar.");
        if (res.status === 429) setCooldown(60);
        return;
      }
      toast.success("Novo código enviado para seu e-mail.");
      setCooldown(60);
      setDigits(["", "", "", "", "", ""]);
      inputs.current[0]?.focus();
    } catch {
      toast.error("Erro ao reenviar código.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-8" data-testid="twofa-card">
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#FF6B00] to-[#F43F5E] flex items-center justify-center shadow-lg mb-4">
          <ShieldCheck className="w-9 h-9 text-white" strokeWidth={2.4} />
        </div>
        <h2 className="font-heading text-2xl font-extrabold text-gray-900" data-testid="twofa-title">
          Verificação em 2 etapas
        </h2>
        <p className="text-sm text-gray-500 mt-2 flex items-center justify-center gap-1.5">
          <Mail className="w-4 h-4" />
          Enviamos um código para <strong className="text-gray-800">{maskedEmail}</strong>
        </p>
      </div>

      <form onSubmit={verify} className="space-y-5">
        <div className="flex justify-center gap-2" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              type="tel"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
              className="w-12 h-14 text-center text-2xl font-extrabold border-2 border-gray-200 rounded-xl focus:border-[#FF6B00] focus:outline-none focus:ring-2 focus:ring-[#FF6B00]/20"
              data-testid={`twofa-digit-${i}`}
              autoComplete={i === 0 ? "one-time-code" : "off"}
            />
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="w-4 h-4 rounded accent-[#FF6B00]"
            data-testid="twofa-remember"
          />
          Confiar neste dispositivo por 30 dias
        </label>

        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-[#FF6B00] to-orange-500 hover:from-[#FF6B00]/90 text-white font-bold py-6 rounded-full"
          disabled={loading || digits.some((d) => !d)}
          data-testid="twofa-verify-button"
        >
          {loading ? "Verificando..." : "Verificar e entrar"}
        </Button>
      </form>

      <div className="mt-5 flex items-center justify-between">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs font-bold text-gray-500 hover:text-gray-700"
          data-testid="twofa-cancel"
        >
          Voltar ao login
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0 || loading}
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#FF6B00] disabled:text-gray-400"
          data-testid="twofa-resend"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          {cooldown > 0 ? `Reenviar em ${cooldown}s` : "Reenviar código"}
        </button>
      </div>
    </div>
  );
}
